import { helpLabelFor } from "./helpIcons";

// `link` is not validated server-side — it can be any string an admin typed,
// including something that isn't a URL at all. Everything that renders an
// anchor goes through here first so a junk value degrades to a non-clickable
// row instead of producing a broken or dangerous href.
export const safeUrl = (link) => {
  if (typeof link !== "string" || !link.trim()) return null;
  try {
    const url = new URL(link.trim());
    return /^https?:$/.test(url.protocol) ? url : null;
  } catch {
    return null;
  }
};

const YT_HOSTS = /(^|\.)(youtube\.com|youtube-nocookie\.com|youtu\.be)$/i;
const VIMEO_HOSTS = /(^|\.)vimeo\.com$/i;

// YouTube ids are 11 chars; anything else came from a path we misread.
const asYouTubeId = (value) => (/^[\w-]{11}$/.test(value || "") ? value : null);

/**
 * Identifies the video behind a tutorial link so a poster frame can be shown.
 * Returns { provider, id } or null when the link isn't a recognized video.
 */
export const parseVideoLink = (link) => {
  const url = safeUrl(link);
  if (!url) return null;

  const { hostname, pathname, searchParams } = url;
  const segments = pathname.split("/").filter(Boolean);

  if (YT_HOSTS.test(hostname)) {
    // youtu.be/<id>
    if (/youtu\.be$/i.test(hostname)) {
      const id = asYouTubeId(segments[0]);
      return id ? { provider: "youtube", id } : null;
    }
    // youtube.com/watch?v=<id>
    const queryId = asYouTubeId(searchParams.get("v"));
    if (queryId) return { provider: "youtube", id: queryId };

    // youtube.com/{embed,shorts,live,v}/<id>
    if (/^(embed|shorts|live|v)$/i.test(segments[0] || "")) {
      const id = asYouTubeId(segments[1]);
      if (id) return { provider: "youtube", id };
    }
    return null;
  }

  if (VIMEO_HOSTS.test(hostname)) {
    // vimeo.com/<id>, vimeo.com/<id>/<hash>, player.vimeo.com/video/<id>
    const numeric = segments.find((s) => /^\d+$/.test(s));
    return numeric ? { provider: "vimeo", id: numeric, url: url.href } : null;
  }

  return null;
};

/**
 * Poster URL for a parsed video, when it can be built without a network call.
 * YouTube serves a predictable path; Vimeo needs an oEmbed lookup, so that one
 * is resolved lazily by useVideoThumbnail instead.
 */
export const staticThumbnailUrl = (video) =>
  video?.provider === "youtube"
    ? `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`
    : null;

// Vimeo poster URLs aren't derivable from the video id, so each one costs an
// oEmbed request. Cache by id for the life of the page — the help content is
// small and static enough that re-requesting on every re-render is pure waste.
const vimeoCache = new Map();

export const fetchVimeoThumbnail = (video) => {
  if (!video || video.provider !== "vimeo") return Promise.resolve(null);
  if (vimeoCache.has(video.id)) return Promise.resolve(vimeoCache.get(video.id));

  const endpoint = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
    video.url
  )}&width=480`;

  // Deliberately a bare fetch, not httpClient: this is a third-party endpoint
  // that must not carry our Authorization header.
  const request = fetch(endpoint)
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      const thumb = data?.thumbnail_url || null;
      vimeoCache.set(video.id, thumb);
      return thumb;
    })
    // A private/removed video or an offline network is not an error worth
    // surfacing — the card falls back to its icon tile.
    .catch(() => {
      vimeoCache.set(video.id, null);
      return null;
    });

  vimeoCache.set(video.id, request);
  return request;
};

/**
 * Groups records by `iconId`, preserving first-appearance order.
 *
 * The API sorts by `sortOrder` ascending across the whole collection before we
 * see it, so relative order inside each group is already correct and the order
 * groups first appear in reflects how the admin laid the list out.
 */
export const groupByIcon = (items = []) => {
  const groups = [];
  const index = new Map();

  items.forEach((item) => {
    const iconId = item?.iconId || "other";
    if (!index.has(iconId)) {
      index.set(iconId, groups.length);
      groups.push({ iconId, label: helpLabelFor(iconId), items: [] });
    }
    groups[index.get(iconId)].items.push(item);
  });

  return groups;
};

/** Case-insensitive match of a search term against a record's text fields. */
export const matchesSearch = (item, term, fields) => {
  if (!term) return true;
  const needle = term.trim().toLowerCase();
  if (!needle) return true;

  const haystack = [...fields.map((f) => item?.[f]), helpLabelFor(item?.iconId)]
    .filter((v) => typeof v === "string")
    .join(" ")
    .toLowerCase();

  return haystack.includes(needle);
};
